document.querySelectorAll( '.b-header' ).forEach( ( header ) => {

	// -------------------------------------------------------------------------
	// Sticky header — Model B
	// -------------------------------------------------------------------------
	// Both the top-bar and the nav row (.b-header__inner-wrap) below it are
	// position:fixed (always visible) — .b-header itself only reserves the
	// space they occupy via padding-top, so their rendered heights are
	// measured via ResizeObserver and exposed as --top-bar-height/--nav-height
	// rather than hard-coded.

	const innerWrap = header.querySelector( '.b-header__inner-wrap' );

	if ( innerWrap ) {

		/**
		 * Measure the nav row's rendered height into --nav-height.
		 *
		 * @return {void}
		 */
		const setNavHeight = () => {

			header.style.setProperty( '--nav-height', `${ innerWrap.offsetHeight }px` );

		};

		setNavHeight();
		new ResizeObserver( setNavHeight ).observe( innerWrap );

	}

	const topBar = header.querySelector( '.b-top-bar' );
	if ( topBar ) {

		/**
		 * Measure the top-bar's rendered height into --top-bar-height.
		 *
		 * @return {void}
		 */
		const setTopBarHeight = () => {

			header.style.setProperty( '--top-bar-height', `${ topBar.offsetHeight }px` );

		};

		const TOP_BAR_DISMISSED_KEY = 'wombat-top-bar-dismissed';

		if ( sessionStorage.getItem( TOP_BAR_DISMISSED_KEY ) === '1' ) {

			topBar.hidden = true;

		}

		setTopBarHeight();
		new ResizeObserver( setTopBarHeight ).observe( topBar );

		const closeButton = topBar.querySelector( '.b-top-bar__close' );

		if ( closeButton ) {

			closeButton.addEventListener( 'click', () => {

				topBar.hidden = true;
				sessionStorage.setItem( TOP_BAR_DISMISSED_KEY, '1' );
				setTopBarHeight();

			} );

		}

	}

	// Switch the top bar + nav row to position:fixed only now that their
	// heights are already measured into --top-bar-height/--nav-height above
	// — .b-header's padding-top (added by this same class) reserves the
	// exact right amount of space from the very first frame it applies,
	// so there's no visible jump/CLS between the normal-flow and fixed state.
	header.classList.add( 'b-header--fixed' );

	// A sentinel element placed right after the header triggers a subtle
	// "stuck" shadow on the nav row via IntersectionObserver — avoids scroll
	// event overhead. It no longer hides the top-bar (Model B: always visible).

	const sentinel = document.createElement( 'div' );
	sentinel.className = 'b-header__sentinel';
	sentinel.setAttribute( 'aria-hidden', 'true' );
	header.after( sentinel );

	const stickyObserver = new IntersectionObserver(
		( [ entry ] ) => {

			const isSticky = ! entry.isIntersecting;
			header.classList.toggle( 'b-header--sticky', isSticky );

			if ( ! isSticky ) {

				header.classList.remove( 'b-header--nav-hidden' );

			}

		},
		{ threshold: 0.5 }
	);
	stickyObserver.observe( sentinel );

	// Once stuck to the top, hide the nav row on scroll-down and reveal it
	// again on scroll-up — only while --sticky is active (before that, the
	// nav is still in its normal top-of-page position and stays visible).
	let lastScrollY = window.scrollY;
	let scrollTicking = false;

	/**
	 * Whether the mobile navigation or its drill-down submenu is currently open.
	 *
	 * @return {boolean} True when mobile navigation UI is active.
	 */
	const isMobileNavigationActive = () => header.classList.contains( 'b-header--menu-open' )
		|| header.classList.contains( 'b-header--submenu-open' );

	window.addEventListener( 'scroll', () => {

		if ( scrollTicking ) {

			return;

		}

		scrollTicking = true;

		window.requestAnimationFrame( () => {

			if ( isMobileNavigationActive() ) {

				header.classList.remove( 'b-header--nav-hidden' );
				lastScrollY = window.scrollY;
				scrollTicking = false;
				return;

			}

			if ( header.classList.contains( 'b-header--sticky' ) ) {

				const currentScrollY = window.scrollY;

				header.classList.toggle( 'b-header--nav-hidden', currentScrollY > lastScrollY );

				lastScrollY = currentScrollY;

			} else {

				lastScrollY = window.scrollY;

			}

			scrollTicking = false;

		} );

	}, { passive: true } );

	// -------------------------------------------------------------------------
	// Hamburger / mobile menu
	// -------------------------------------------------------------------------

	const hamburger = header.querySelector( '.js-hamburger' );
	if ( hamburger ) {

		hamburger.addEventListener( 'click', () => {

			const isOpen = header.classList.toggle( 'b-header--menu-open' );
			header.classList.remove( 'b-header--nav-hidden' );
			hamburger.setAttribute( 'aria-expanded', String( isOpen ) );
			// Trap scroll on body when mobile menu is open
			document.body.classList.toggle( 'u-overflow-hidden', isOpen );

			if ( isOpen ) {

				const wrapper = header.querySelector( '.b-nav-megamenu-wrapper' );
				const firstFocusable = wrapper?.querySelector( 'a[href], button:not([disabled])' );
				firstFocusable?.focus();

			} else {

				// Reset all submenus when mobile menu closes
				closeAllDropdowns();

			}

		} );

	}

	/**
	 * Collect focusable, visible elements within a container.
	 *
	 * @param {Element} container Element to search within.
	 * @return {Element[]} Focusable elements, in DOM order.
	 */
	const getFocusableElements = ( container ) => Array.from(
		container.querySelectorAll( 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])' )
	).filter( ( el ) => el.offsetParent !== null );

	// Close mobile menu on Escape
	document.addEventListener( 'keydown', ( e ) => {

		if ( e.key === 'Escape' && header.classList.contains( 'b-header--menu-open' ) ) {

			header.classList.remove( 'b-header--menu-open' );
			document.body.classList.remove( 'u-overflow-hidden' );
			if ( hamburger ) {

				hamburger.setAttribute( 'aria-expanded', 'false' );
				hamburger.focus();

			}

		}

	} );

	// Focus trap: while the mobile fullscreen menu is open, Tab/Shift+Tab
	// cycle only through focusable elements inside the header.
	document.addEventListener( 'keydown', ( e ) => {

		if ( e.key !== 'Tab' || ! header.classList.contains( 'b-header--menu-open' ) ) {

			return;

		}

		const focusable = getFocusableElements( header );
		if ( ! focusable.length ) {

			return;

		}

		const first = focusable[ 0 ];
		const last = focusable[ focusable.length - 1 ];
		const activeElement = header.ownerDocument.activeElement;

		if ( e.shiftKey && activeElement === first ) {

			e.preventDefault();
			last.focus();

		} else if ( ! e.shiftKey && activeElement === last ) {

			e.preventDefault();
			first.focus();

		}

	} );

	// -------------------------------------------------------------------------
	// Megamenu dropdowns (desktop)
	// -------------------------------------------------------------------------

	const dropdownTriggers = header.querySelectorAll( '.js-megamenu-dropdown' );

	/**
	 * Resolve the dropdown panel controlled by a trigger via aria-controls.
	 *
	 * @param {Element} trigger Dropdown trigger button.
	 * @return {Element|null} The panel element, or null when not found.
	 */
	const getDropdownPanel = ( trigger ) => {

		const panelId = trigger.getAttribute( 'aria-controls' );
		return panelId ? document.getElementById( panelId ) : null;

	};

	/**
	 * Close all open dropdown menus, optionally excluding one trigger.
	 *
	 * @param {Element|null} except Trigger element to leave open.
	 * @return {void}
	 */
	const closeAllDropdowns = ( except = null ) => {

		dropdownTriggers.forEach( ( trigger ) => {

			if ( trigger === except ) {

				return;

			}
			trigger.setAttribute( 'aria-expanded', 'false' );
			trigger.closest( '.b-nav-megamenu-item' )?.classList.remove( 'b-nav-megamenu-item--open' );

			const panel = getDropdownPanel( trigger );
			if ( panel ) {

				panel.hidden = true;

			}

		} );

		const anyOpen = !! header.querySelector( '.b-nav-megamenu-item--open' );
		header.classList.toggle( 'b-header--submenu-open', anyOpen );
		header.classList.remove( 'b-header--nav-hidden' );

	};

	dropdownTriggers.forEach( ( trigger ) => {

		trigger.addEventListener( 'click', ( e ) => {

			e.stopPropagation();
			const item = trigger.closest( '.b-nav-megamenu-item' );
			const isOpen = item?.classList.toggle( 'b-nav-megamenu-item--open' );

			trigger.setAttribute( 'aria-expanded', String( isOpen ) )

			const panel = getDropdownPanel( trigger );
			if ( panel ) {

				panel.hidden = ! isOpen;

			}

			if ( !header.classList.contains( 'b-header--menu-open' ) ) {

				document.body.classList.toggle( 'u-overflow-hidden', isOpen );

			}

			// Close other open dropdowns
			closeAllDropdowns( trigger );

		} );

		// Keyboard: open on Enter/Space, close on Escape
		trigger.addEventListener( 'keydown', ( e ) => {

			if ( e.key === 'Escape' ) {

				closeAllDropdowns();
				document.body.classList.remove( 'u-overflow-hidden' );
				trigger.focus();

			}

		} );

	} );

	// Mobile drill-down panel controls — back returns to the nav list
	// (closes just that dropdown), close dismisses the whole mobile menu.
	header.addEventListener( 'click', ( e ) => {

		const backButton = e.target.closest( '.js-megamenu-back' );
		if ( backButton ) {

			const panel = backButton.closest( '.b-nav-megamenu-item__panel' );
			const trigger = panel?.id ? header.querySelector( `[aria-controls="${ panel.id }"]` ) : null;

			closeAllDropdowns();
			trigger?.focus();
			return;

		}

		const closeButton = e.target.closest( '.js-megamenu-close' );
		if ( closeButton ) {

			closeAllDropdowns();
			header.classList.remove( 'b-header--menu-open' );
			document.body.classList.remove( 'u-overflow-hidden' );
			hamburger?.setAttribute( 'aria-expanded', 'false' );
			hamburger?.focus();

		}

	} );

	// Close dropdown when clicking outside
	document.addEventListener( 'click', ( e ) => {

		if ( ! header.contains( e.target ) ) {

			closeAllDropdowns();
			header.classList.remove( 'b-header--menu-open' );
			document.body.classList.remove( 'u-overflow-hidden' );
			hamburger.setAttribute( 'aria-expanded', 'false' );

		}

	} );

} );
